import { db, type AppNotification, type NotificationKind } from './database'
import { deleteLocal, writeLocal } from './sync'
import { getProfile } from './auth'
import { newId } from '../lib/uuid'
import { now } from '../lib/dates'

/**
 * Le centre de notifications.
 *
 * ⚠️ **Une notification est une TRACE, jamais une alarme.** Le Web ne sait pas
 * programmer un réveil pour une date future (décision 2026-09-05, `vision.md`,
 * « Distribution — la PWA est le seul artefact ») : ce qui est écrit ici se
 * découvre à l'ouverture de l'app, éventuellement des jours plus tard. Aucune
 * information ne doit donc vivre UNIQUEMENT dans une notification — elle
 * redit, elle n'annonce pas.
 *
 * Le jour où le push serveur existera, il poussera exactement ces lignes-là :
 * c'est la même liste, vue d'un autre bout.
 *
 * ## Une boîte par personne
 *
 * ⚠️ Tout ici est scopé par **compte**, jamais par portefeuille — c'est la
 * seule entité synchronisée dans ce cas. Deux membres d'un même portefeuille
 * ont deux boîtes distinctes : sans quoi le premier qui lit marquerait lu pour
 * l'autre. Le serveur applique la même frontière et refuse toute écriture dans
 * la boîte d'autrui (`Sync::Push#allowed_for_account?`).
 *
 * Écrire passe donc par `writeLocal` / `deleteLocal` comme partout : une
 * écriture directe dans Dexie ne partirait jamais au serveur, et la
 * notification lue ici resterait non lue sur l'autre appareil.
 */

/**
 * À qui s'adressent les notifications de cet appareil.
 *
 * `null` quand aucun compte n'est encore connu — l'app exige un compte au
 * premier lancement, mais les tests, eux, posent un jeton sans profil. Rien ne
 * s'écrit et rien ne s'affiche dans ce cas : une notification sans destinataire
 * ne pourrait pas se synchroniser.
 */
export function currentAccountId(): string | null {
  return getProfile()?.id ?? null
}

export interface NewNotification {
  kind: NotificationKind
  title: string
  body?: string | null
  /** Le portefeuille d'où vient l'événement. `null` = elle n'en relève d'aucun. */
  walletId?: string | null
  /** L'écran à ouvrir quand on la touche. */
  route?: string | null
  /**
   * Quand l'ÉVÉNEMENT a eu lieu. Par défaut maintenant, mais un rappel
   * rattrapé à l'ouverture doit porter la date de son échéance, pas celle du
   * moment où l'app a été rouverte.
   */
  occurredAt?: string
}

/**
 * Écrire une notification, adressée au compte de cet appareil.
 *
 * Renvoie `null` si aucun compte n'est connu — rien n'est écrit plutôt que
 * d'écrire une ligne que la synchronisation refuserait.
 *
 * Pour tout ce qui se RECALCULE — une enveloppe dépassée, une journée à
 * confirmer — c'est `ensureNotification` qu'il faut, avec un identifiant
 * dérivé : celle-ci fabrique un identifiant neuf à chaque appel, et le
 * rattrapage écrirait donc une ligne de plus à chaque ouverture de l'app.
 */
export async function notify(
  { kind, title, body = null, walletId = null, route = null, occurredAt }: NewNotification,
): Promise<string | null> {
  const accountId = currentAccountId()
  if (!accountId) return null

  const id = newId()
  await writeLocal<AppNotification>('notifications', {
    id,
    account_id: accountId,
    wallet_id: walletId,
    kind,
    title,
    body,
    route,
    occurred_at: occurredAt ?? now().toISOString(),
    read_at: null,
    // Posés par `writeLocal`, qui est seul à tenir l'horloge d'arbitrage.
    _version: 0,
    client_updated_at: '',
    deleted_at: null,
  })
  return id
}

/**
 * Écrire une notification À IDENTIFIANT IMPOSÉ, une seule fois.
 *
 * C'est ce qui rend le rattrapage (`db/notification-rules.ts`) rejouable : il
 * tourne à chaque ouverture de l'app, et doit pouvoir redire dix fois la même
 * chose sans jamais écrire deux lignes.
 *
 * ⚠️ **Un tombstone bloque la recréation.** Une notification supprimée par
 * l'utilisateur ne doit JAMAIS revenir : l'enveloppe est toujours dépassée, la
 * règle se redéclencherait donc à chaque réouverture, et l'app deviendrait
 * insupportable. Écarter est un choix, il se respecte.
 *
 * Rien n'est réécrit non plus quand la ligne existe déjà, même modifiée : la
 * repousser à chaque ouverture remplirait la file d'envoi de doublons inutiles,
 * et écraserait l'état lu venu d'un autre appareil.
 */
export async function ensureNotification(
  id: string, { kind, title, body = null, walletId = null, route = null, occurredAt }: NewNotification,
): Promise<boolean> {
  const accountId = currentAccountId()
  if (!accountId) return false
  if (await db.notifications.get(id)) return false

  await writeLocal<AppNotification>('notifications', {
    id,
    account_id: accountId,
    wallet_id: walletId,
    kind,
    title,
    body,
    route,
    occurred_at: occurredAt ?? now().toISOString(),
    read_at: null,
    _version: 0,
    client_updated_at: '',
    deleted_at: null,
  })
  return true
}

/** Les notifications du compte, de la plus récente à la plus ancienne. */
export async function listNotifications(accountId: string): Promise<AppNotification[]> {
  const all = await db.notifications.where({ account_id: accountId }).toArray()
  return all
    // Une ligne supprimée reste en base : c'est un tombstone, sans quoi la
    // suppression ne se propagerait pas aux autres appareils.
    .filter((n) => !n.deleted_at)
    // Décroissant : ce qui vient d'arriver est ce qu'on cherche. L'identifiant
    // départage — un UUIDv7 porte sa date de création, qui ne bouge jamais.
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at) || b.id.localeCompare(a.id))
}

/** Combien de notifications attendent d'être lues. C'est ce que porte la pastille. */
export async function unreadCount(accountId: string): Promise<number> {
  const all = await listNotifications(accountId)
  // `?? null` : une ligne écrite avant l'existence du champ vaudrait `undefined`.
  return all.filter((n) => (n.read_at ?? null) === null).length
}

/**
 * Marquer lue, ou non lue.
 *
 * Les deux sens comptent : rendre une notification à l'état non lu est la
 * seule façon de se la remettre en tête quand on n'a pas le temps d'agir tout
 * de suite. C'est ce que fait le glissement vers la gauche, dans les deux sens.
 */
export async function setRead(id: string, read: boolean): Promise<void> {
  const existing = await db.notifications.get(id)
  if (!existing) return
  await writeLocal<AppNotification>('notifications', {
    ...existing,
    read_at: read ? now().toISOString() : null,
  })
}

export async function markAllRead(accountId: string): Promise<void> {
  const unread = (await listNotifications(accountId))
    .filter((n) => (n.read_at ?? null) === null)
  const at = now().toISOString()
  // Une écriture par ligne, et non un `bulkPut` : chacune doit produire sa
  // propre opération dans la file d'envoi, sinon rien ne part au serveur.
  for (const notification of unread) {
    await writeLocal<AppNotification>('notifications', { ...notification, read_at: at })
  }
}

/** Soft-delete, comme partout : jamais de suppression physique. */
export async function removeNotification(id: string): Promise<void> {
  await deleteLocal('notifications', id)
}
